/**
 * WhoToReach (34i87, Seth 2026-10-21) — the Confirm step's one question: who do
 * we tell, and where. Three jobs, each pointed at one line:
 *   · the CONFIRMATION TEXT (sent the moment it is booked, and the day-before
 *     reminder) → exactly one phone (a radio: the best mobile by default)
 *   · the HEADS-UP on the way (the tech, day-of) → none or one phone, by
 *     call or text ("call one number and/or text another" — two pills on two
 *     rows, that is all it takes)
 *   · an EMAIL for the visit → none or one address (kept on the visit; an
 *     email confirmation is its own automation, not on yet — APPT-CONFIRM-EMAIL)
 * The people come from the same place the header and the rail draw them
 * (contactLines: Pegasus contacts → ESC's grid → the phone bank), after the
 * owner era, so a sold house never offers the previous owner's numbers. A
 * one-off person is typed in place; "Save to the record" (on by default when
 * they have a name) makes them a Pegasus contact when the visit is booked.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Mail, Plus, UserRound, Building2, X, ChevronDown, Check, Phone } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { pegasusToCustomer, type CustomerWithLocations, type LocationRow, type PegasusCustomerRow } from "./api";
import { useContacts } from "./CustomerContacts";
import { useLocationOwnership } from "@/features/jobs/properties";
import { buildLines, contactEraOf, type Line } from "./contactLines";

const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);
export const prettyPhone = (s: string) => { const d = p10(s); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s; };
const dashPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface OtherPerson { name: string; role: string; phone: string; email: string; save: boolean }
export interface ReachState {
  /** The phone line the confirmation text + day-before reminder go to. */
  confirmKey: string | null;
  /** The phone line the tech's on-the-way heads-up goes to (null = none). */
  headsUpKey: string | null;
  headsUpMethod: "call" | "text";
  /** The email kept on the visit (null = none). */
  emailKey: string | null;
  /** Someone not on the record yet (key "other"). */
  other: OtherPerson | null;
  /** An email typed for a person who had none — keyed by their line. */
  addedEmail: Record<string, { email: string; save: boolean }>;
}
export const EMPTY_REACH: ReachState = { confirmKey: null, headsUpKey: null, headsUpMethod: "call", emailKey: null, other: null, addedEmail: {} };

export interface ReachPerson {
  key: string;
  origin: Line["origin"] | "other";
  name: string | null;
  role: string | null;
  phone: string | null;
  ext: string | null;
  email: string | null;
  line: Line | null;
}

/** The people on the account as Book a Call sees them — one query each for the mirror rows the line-builder needs. */
export function useReachPeople(custno: string | null, locno: string | null) {
  const { data: cust } = useQuery({
    queryKey: ["reach_cust", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_customer").select("*").eq("custno", custno!).maybeSingle();
      if (data) return data as CustomerWithLocations;
      // RECORD-READ-MODEL (34i97): a Pegasus-born customer has no mirror row — their own lines still count.
      const { data: pc } = await supabase.from("pegasus_customer").select("*").eq("custno", custno!).maybeSingle();
      return pc ? pegasusToCustomer(pc as PegasusCustomerRow) : null;
    },
  });
  const { data: loc } = useQuery({
    queryKey: ["reach_loc", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_location").select("*").eq("custno", custno!).eq("locno", locno ?? "00001").limit(1).maybeSingle();
      return (data ?? null) as LocationRow | null;
    },
  });
  const contacts = useContacts(custno ?? "", locno).data ?? [];
  const { data: owned } = useLocationOwnership(custno, locno);
  const era = useMemo(() => contactEraOf(owned), [owned]);
  const lines = useMemo<Line[]>(() => {
    if (!cust) return [];
    return buildLines({ ...cust, locations: cust.locations ?? [] }, loc ?? null, contacts, era);
  }, [cust, loc, contacts, era]);
  return { lines, era, ready: cust !== undefined };
}

/** Lines + the typed one-off → the rows the picker shows. */
export function reachPeople(lines: Line[], other: OtherPerson | null): ReachPerson[] {
  const out: ReachPerson[] = lines.map((l) => ({ key: l.key, origin: l.origin, name: l.name, role: l.role, phone: l.phone, ext: l.ext, email: l.email, line: l }));
  if (other) {
    const phone = other.phone.replace(/\D/g, "").length === 10 ? other.phone : null;
    const email = EMAIL_RE.test(other.email.trim()) ? other.email.trim() : null;
    out.push({ key: "other", origin: "other", name: other.name.trim() || null, role: other.role.trim() || null, phone, ext: null, email, line: null });
  }
  return out;
}

/** The best line for the confirmation text: a named mobile first, a landline label last. */
export function defaultConfirmKey(lines: Line[], hint?: string | null): string | null {
  const withPhone = lines.filter((l) => l.phone);
  if (withPhone.length === 0) return null;
  const score = (l: Line) => {
    let s = 0;
    if (hint && p10(hint) && l.phone && p10(l.phone) === p10(hint)) s += 2;
    if (/cell|mobile|text/i.test(l.role ?? "")) s += 3;
    if (l.origin === "pegasus-owner") s += 2;
    if (l.origin === "pegasus") s += 1;
    if (/fax|work|office|land/i.test(l.role ?? "")) s -= 2;
    return s;
  };
  return [...withPhone].sort((a, b) => score(b) - score(a))[0].key;
}

export interface ReachResolved {
  confirm: { phone: string; name: string | null } | null;
  headsUp: { phone: string; name: string | null; method: "call" | "text" } | null;
  email: { email: string; name: string | null } | null;
}
/** What the booking writes, from the picks. */
export function resolveReach(people: ReachPerson[], st: ReachState): ReachResolved {
  const byKey = new Map(people.map((p) => [p.key, p]));
  const c = st.confirmKey ? byKey.get(st.confirmKey) : null;
  const h = st.headsUpKey ? byKey.get(st.headsUpKey) : null;
  const typedOf = (p: ReachPerson) => { const t = st.addedEmail[p.key]?.email?.trim(); return t && EMAIL_RE.test(t) ? t : null; };
  const emailOf = (p: ReachPerson | null | undefined) => p ? (typedOf(p) ?? p.email ?? null) : null;
  const e = st.emailKey ? byKey.get(st.emailKey) : null;
  const em = emailOf(e);
  return {
    confirm: c?.phone ? { phone: c.phone, name: c.name } : null,
    headsUp: h?.phone ? { phone: h.phone, name: h.name, method: st.headsUpMethod } : null,
    email: e && em && EMAIL_RE.test(em) ? { email: em, name: e.name } : null,
  };
}

/** The short lines for the summary rail. */
export function reachSummary(r: ReachResolved): string[] {
  const who = (n: string | null) => (n ? `${n.split(/\s+/)[0]} ` : "");
  const out: string[] = [];
  if (r.confirm) out.push(`Text ${who(r.confirm.name)}${prettyPhone(r.confirm.phone)}`);
  if (r.headsUp) out.push(`${r.headsUp.method === "text" ? "Text" : "Call"} ${who(r.headsUp.name)}${prettyPhone(r.headsUp.phone)} on the way`);
  if (r.email) out.push(`Email ${r.email.email}`);
  return out;
}

/** After the visit is booked: the one-off person and any typed emails become Pegasus contacts. Best-effort. */
export async function persistReach(custno: string, locno: string | null, people: ReachPerson[], st: ReachState, qc: ReturnType<typeof useQueryClient>) {
  try {
    const { data: u } = await supabase.auth.getUser();
    const by = u.user?.email ?? null;
    let wrote = false;
    const o = st.other;
    if (o && o.save && o.name.trim() && (o.phone.replace(/\D/g, "").length === 10 || EMAIL_RE.test(o.email.trim()))) {
      await supabase.from("contact").insert({
        custno, locno: null, name: o.name.trim(), role: o.role.trim() || null,
        phone: o.phone.replace(/\D/g, "").length === 10 ? o.phone.trim() : null,
        email: EMAIL_RE.test(o.email.trim()) ? o.email.trim() : null,
        gets_scheduling: true, gets_billing: true, gets_marketing: true, source: "schedule", created_by: by,
      });
      wrote = true;
    }
    for (const [key, add] of Object.entries(st.addedEmail)) {
      if (!add.save || !EMAIL_RE.test(add.email.trim())) continue;
      const p = people.find((x) => x.key === key);
      if (!p) continue;
      if (p.line?.contact) {
        if (p.line.contact.email) continue; // a typed email never replaces the one on the contact — it rides the visit only
        await supabase.from("contact").update({ email: add.email.trim(), updated_at: new Date().toISOString() }).eq("id", p.line.contact.id);
        wrote = true;
      } else if (p.name || p.phone) {
        // An ESC line gets promoted into a Pegasus contact carrying the new email.
        await supabase.from("contact").insert({
          custno, locno: locno ?? null, name: p.name ?? (p.role ? `${p.role}` : "Contact"), role: p.name ? p.role : null,
          phone: p.phone, email: add.email.trim(),
          gets_scheduling: true, gets_billing: true, gets_marketing: true, source: "schedule", created_by: by,
        });
        wrote = true;
      }
    }
    if (wrote) {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["cust_call_numbers", custno] });
    }
  } catch { /* the visit is what matters; the People card can add them later */ }
}

/* ----------------------------- the picker ----------------------------- */
/**
 * Three slots, one question each — WHO gets the confirmation text, WHO gets
 * the heads-up on the way (and how), WHICH email rides the visit. Each slot
 * opens a short menu of the people on the record; the people themselves sit
 * under the slots with their lines, so the dispatcher can read the whole
 * picture before touching anything (Seth 2026-10-21: the per-line pills were
 * "messy and confusing").
 */

const initialsOf = (name: string | null) => (name ?? "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

function personLabel(p: ReachPerson): string {
  return p.name ?? p.role ?? (p.phone ? "Phone on file" : "Email on file");
}

function Avatar({ p, size = "md" }: { p: ReachPerson; size?: "sm" | "md" }) {
  const esc = p.origin === "esc-property" || p.origin === "esc-account";
  return (
    <span className={cn("grid shrink-0 place-items-center rounded-full font-bold", size === "sm" ? "h-7 w-7 text-[10.5px]" : "h-9 w-9 text-[12px]", esc ? "bg-slate-100 text-slate-500" : "bg-brand-50 text-brand-700")}>
      {p.name ? initialsOf(p.name) : esc ? <Building2 className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />}
    </span>
  );
}

/** One slot: the label, the chosen line as a bold fact, a menu on click. */
function Slot({ label, hint, chosen, none, open, onToggle, onClose, children, accessory }: {
  label: string;
  hint: string;
  chosen: { person: ReachPerson; line: string } | null;
  /** What the slot reads when nothing is chosen. */
  none: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: ReactNode;
  /** A control beside the value (the Call | Text switch). */
  accessory?: ReactNode;
}) {
  return (
    <div className={cn("relative rounded-2xl border bg-white p-3.5 transition-colors", chosen ? "border-hairline" : "border-dashed border-slate-300")}>
      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <button type="button" onClick={onToggle} aria-expanded={open} className="mt-2 flex w-full items-center gap-2.5 rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30">
        {chosen ? (
          <>
            <Avatar p={chosen.person} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-bold leading-tight text-slate-900">{personLabel(chosen.person)}{chosen.person.name && chosen.person.role ? <span className="ml-1.5 text-[11px] font-semibold text-slate-400">{chosen.person.role}</span> : null}</span>
              <span className="block truncate text-[13px] font-medium tabular-nums text-slate-600">{chosen.line}</span>
            </span>
          </>
        ) : (
          <>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-dashed border-slate-300 text-slate-300"><Plus className="h-4 w-4" /></span>
            <span className="min-w-0 flex-1 text-[14px] font-semibold text-slate-400">{none}</span>
          </>
        )}
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11.5px] text-slate-400">{hint}</span>
        {accessory}
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={onClose} />
          <div role="listbox" className="absolute left-2 right-2 top-[calc(100%-6px)] z-30 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-e2">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function MenuRow({ p, line, on, onPick, sub }: { p: ReachPerson | null; line: string; on: boolean; onPick: () => void; sub?: string | null }) {
  return (
    <button type="button" role="option" aria-selected={on} onClick={onPick}
      className={cn("flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors", on ? "bg-brand-50" : "hover:bg-slate-50")}>
      {p ? <Avatar p={p} size="sm" /> : <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-400"><X className="h-3.5 w-3.5" /></span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-slate-800">{p ? personLabel(p) : line}{p && sub ? <span className="ml-1.5 text-[11px] font-medium text-slate-400">{sub}</span> : null}</span>
        {p && <span className="block truncate text-[12.5px] tabular-nums text-slate-500">{line}</span>}
      </span>
      {on && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
    </button>
  );
}

export function WhoToReach({ people, state, onChange, canSave = true }: {
  people: ReachPerson[];
  state: ReachState;
  onChange: (next: ReachState) => void;
  /** contacts.edit — whether "Save to the record" is offered at all. */
  canSave?: boolean;
}) {
  const [openSlot, setOpenSlot] = useState<"confirm" | "heads" | "email" | null>(null);
  const [typingEmail, setTypingEmail] = useState(false);
  const set = (patch: Partial<ReachState>) => onChange({ ...state, ...patch });
  const other = state.other;
  const byKey = new Map(people.map((p) => [p.key, p]));
  const phones = people.filter((p) => p.phone);
  const typedOf = (p: ReachPerson) => { const t = state.addedEmail[p.key]?.email?.trim(); return t && EMAIL_RE.test(t) ? t : null; };
  const emailOf = (p: ReachPerson) => typedOf(p) ?? p.email ?? null;
  const emails = people.filter((p) => emailOf(p));
  const confirmP = state.confirmKey ? byKey.get(state.confirmKey) ?? null : null;
  const headsP = state.headsUpKey ? byKey.get(state.headsUpKey) ?? null : null;
  const emailP = state.emailKey ? byKey.get(state.emailKey) ?? null : null;
  const toggle = (k: "confirm" | "heads" | "email") => setOpenSlot((cur) => (cur === k ? null : k));
  const openOther = () => { if (!other) set({ other: { name: "", role: "", phone: "", email: "", save: canSave } }); setOpenSlot(null); };
  // The typed email belongs to someone — the confirmation person by default.
  const [emailFor, setEmailFor] = useState<string | null>(null);
  const typedOwner = emailFor ?? people.find((p) => (p.name || p.phone) && !p.email)?.key ?? state.confirmKey ?? people.find((p) => p.name)?.key ?? null;
  const typed = typedOwner ? state.addedEmail[typedOwner] : undefined;
  const ownerHasEmail = !!(typedOwner && byKey.get(typedOwner)?.email);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-slate-500">Who to Reach</span>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {/* 1 · the confirmation text — exactly one number */}
        <Slot
          label="Confirmation Text"
          hint="Sent when booked · reminder the day before"
          chosen={confirmP?.phone ? { person: confirmP, line: prettyPhone(confirmP.phone) } : null}
          none={phones.length ? "Pick a number" : "No number on file"}
          open={openSlot === "confirm"}
          onToggle={() => toggle("confirm")}
          onClose={() => setOpenSlot(null)}
        >
          {phones.map((p) => (
            <MenuRow key={p.key} p={p} line={prettyPhone(p.phone!)} sub={p.name ? p.role : null} on={state.confirmKey === p.key} onPick={() => { set({ confirmKey: p.key }); setOpenSlot(null); }} />
          ))}
          <button type="button" onClick={openOther} className="flex w-full items-center gap-2.5 border-t border-slate-100 px-3 py-2 text-left text-[13px] font-semibold text-brand-700 hover:bg-brand-50"><Plus className="h-4 w-4" /> Someone else…</button>
        </Slot>

        {/* 2 · the heads-up on the way — none or one, call or text */}
        <Slot
          label="Heads-Up on the Way"
          hint={headsP ? "The tech reaches them before arriving" : "No heads-up"}
          chosen={headsP?.phone ? { person: headsP, line: prettyPhone(headsP.phone) } : null}
          none="Add a heads-up"
          open={openSlot === "heads"}
          onToggle={() => toggle("heads")}
          onClose={() => setOpenSlot(null)}
          accessory={headsP ? (
            <span className="inline-flex h-7 rounded-full border border-brand-200 bg-brand-50 p-0.5">
              {(["call", "text"] as const).map((m) => (
                <button key={m} type="button" onClick={() => set({ headsUpMethod: m })} aria-pressed={state.headsUpMethod === m}
                  className={cn("inline-flex items-center gap-1 rounded-full px-2.5 text-[11.5px] font-bold transition-colors", state.headsUpMethod === m ? "bg-white text-brand-800 shadow-sm" : "text-brand-600 hover:text-brand-800")}>
                  {m === "call" ? <Phone className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}{m === "call" ? "Call" : "Text"}
                </button>
              ))}
            </span>
          ) : null}
        >
          {phones.map((p) => (
            <MenuRow key={p.key} p={p} line={prettyPhone(p.phone!)} sub={p.name ? p.role : null} on={state.headsUpKey === p.key} onPick={() => { set({ headsUpKey: p.key }); setOpenSlot(null); }} />
          ))}
          <MenuRow p={null} line="No heads-up" on={!state.headsUpKey} onPick={() => { set({ headsUpKey: null }); setOpenSlot(null); }} />
          <button type="button" onClick={openOther} className="flex w-full items-center gap-2.5 border-t border-slate-100 px-3 py-2 text-left text-[13px] font-semibold text-brand-700 hover:bg-brand-50"><Plus className="h-4 w-4" /> Someone else…</button>
        </Slot>

        {/* 3 · an email for the visit — none or one */}
        <Slot
          label="Email"
          hint={emailP ? "Kept on the visit" : "No email on the visit"}
          chosen={emailP && emailOf(emailP) ? { person: emailP, line: emailOf(emailP)! } : null}
          none="Add an email"
          open={openSlot === "email"}
          onToggle={() => toggle("email")}
          onClose={() => setOpenSlot(null)}
        >
          {emails.map((p) => (
            <MenuRow key={p.key} p={p} line={emailOf(p)!} sub={p.name ? p.role : null} on={state.emailKey === p.key} onPick={() => { set({ emailKey: p.key }); setOpenSlot(null); }} />
          ))}
          <MenuRow p={null} line="No email" on={!state.emailKey} onPick={() => { set({ emailKey: null }); setOpenSlot(null); }} />
          <button type="button" onClick={() => { setTypingEmail(true); setOpenSlot(null); }} className="flex w-full items-center gap-2.5 border-t border-slate-100 px-3 py-2 text-left text-[13px] font-semibold text-brand-700 hover:bg-brand-50"><Plus className="h-4 w-4" /> Type an email…</button>
        </Slot>
      </div>

      {/* A typed email: whose it is, and whether it goes on the record. */}
      {typingEmail && typedOwner && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-hairline bg-white px-3.5 py-3">
          <Mail className="h-4 w-4 text-slate-400" />
          <input type="email" autoFocus value={typed?.email ?? ""} placeholder="name@example.com"
            onChange={(e) => set({ addedEmail: { ...state.addedEmail, [typedOwner]: { email: e.target.value, save: typed?.save ?? canSave } }, emailKey: EMAIL_RE.test(e.target.value.trim()) ? typedOwner : state.emailKey })}
            className="h-9 w-64 rounded-xl border border-slate-300 px-3 text-[14px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          <span className="text-[13px] text-slate-500">for</span>
          <select value={typedOwner} onChange={(e) => { const k = e.target.value; const cur = typed; const next = { ...state.addedEmail }; delete next[typedOwner]; if (cur) next[k] = cur; setEmailFor(k); set({ addedEmail: next, emailKey: cur && EMAIL_RE.test(cur.email) ? k : state.emailKey }); }}
            className="h-9 rounded-xl border border-slate-300 bg-white px-2.5 text-[13px] font-semibold text-slate-800 focus:border-brand-500 focus:outline-none">
            {people.filter((p) => p.name || p.phone).map((p) => <option key={p.key} value={p.key}>{personLabel(p)}</option>)}
          </select>
          {canSave && typedOwner !== "other" && !ownerHasEmail && (
            <label className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600">
              <input type="checkbox" checked={typed?.save ?? canSave} onChange={(e) => set({ addedEmail: { ...state.addedEmail, [typedOwner]: { email: typed?.email ?? "", save: e.target.checked } } })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400" />
              Save to the record
            </label>
          )}
          {ownerHasEmail && <span className="text-[12.5px] text-slate-400">this visit only · {personLabel(byKey.get(typedOwner)!)} keeps {byKey.get(typedOwner)!.email}</span>}
          <button type="button" onClick={() => { const next = { ...state.addedEmail }; delete next[typedOwner]; set({ addedEmail: next, emailKey: state.emailKey === typedOwner ? null : state.emailKey }); setTypingEmail(false); }} className="ml-auto rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" title="Never mind"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Someone not on the record — typed once, used in any slot, saved if wanted. */}
      {other && (
        <div className="mt-3 rounded-2xl border border-brand-200 bg-brand-50/40 px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-100 text-[12px] font-bold text-brand-700">{initialsOf(other.name) || <UserRound className="h-4 w-4" />}</span>
            <input value={other.name} onChange={(e) => set({ other: { ...other, name: e.target.value } })} placeholder="Name" autoFocus
              className="h-9 w-44 rounded-xl border border-slate-300 px-3 text-[14px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input value={other.role} onChange={(e) => set({ other: { ...other, role: e.target.value } })} placeholder="Daughter, tenant, property manager…"
              className="h-9 w-60 rounded-xl border border-slate-300 px-3 text-[14px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="tel" value={other.phone} onChange={(e) => set({ other: { ...other, phone: dashPhone(e.target.value) } })} placeholder="Phone"
              className="h-9 w-36 rounded-xl border border-slate-300 px-3 text-[14px] tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <input type="email" value={other.email} onChange={(e) => set({ other: { ...other, email: e.target.value } })} placeholder="Email"
              className="h-9 w-56 rounded-xl border border-slate-300 px-3 text-[14px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
            <button type="button" onClick={() => set({ other: null, confirmKey: state.confirmKey === "other" ? null : state.confirmKey, headsUpKey: state.headsUpKey === "other" ? null : state.headsUpKey, emailKey: state.emailKey === "other" ? null : state.emailKey })}
              className="ml-auto rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" title="Remove"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px]">
            {other.phone.replace(/\D/g, "").length === 10 && (
              <>
                <button type="button" onClick={() => set({ confirmKey: "other" })} className={cn("font-semibold", state.confirmKey === "other" ? "text-brand-800" : "text-brand-600 hover:text-brand-800")}>{state.confirmKey === "other" ? "✓ Gets the confirmation text" : "Send the confirmation text here"}</button>
                <button type="button" onClick={() => set({ headsUpKey: state.headsUpKey === "other" ? null : "other" })} className={cn("font-semibold", state.headsUpKey === "other" ? "text-brand-800" : "text-brand-600 hover:text-brand-800")}>{state.headsUpKey === "other" ? "✓ Gets the heads-up" : "Send the heads-up here"}</button>
              </>
            )}
            {EMAIL_RE.test(other.email.trim()) && (
              <button type="button" onClick={() => set({ emailKey: state.emailKey === "other" ? null : "other" })} className={cn("font-semibold", state.emailKey === "other" ? "text-brand-800" : "text-brand-600 hover:text-brand-800")}>{state.emailKey === "other" ? "✓ The visit's email" : "Use this email"}</button>
            )}
            {canSave && (
              <label className="ml-auto inline-flex items-center gap-1.5 font-medium text-slate-600">
                <input type="checkbox" checked={other.save} onChange={(e) => set({ other: { ...other, save: e.target.checked } })} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400" />
                Save to the record{!other.name.trim() && <span className="text-slate-400">· needs a name</span>}
              </label>
            )}
          </div>
        </div>
      )}

      {/* Everyone on the record, with their lines — the picture behind the slots. */}
      {people.filter((p) => p.key !== "other").length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 px-1 text-[12.5px] text-slate-500">
          {people.filter((p) => p.key !== "other").map((p) => (
            <span key={p.key} className="inline-flex items-center gap-1.5">
              <span className="font-semibold text-slate-700">{personLabel(p)}</span>
              {p.name && p.role && <span className="text-slate-400">{p.role}</span>}
              {p.phone && <span className="tabular-nums">{prettyPhone(p.phone)}{p.ext ? ` x${p.ext}` : ""}</span>}
              {emailOf(p) && <span className="truncate">{emailOf(p)}</span>}
            </span>
          ))}
          {!other && (
            <button type="button" onClick={openOther} className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:text-brand-900"><Plus className="h-3.5 w-3.5" /> Someone else</button>
          )}
        </div>
      )}
    </section>
  );
}
